import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './dashboard.css';
import TicketTableDashboard from './TicketTableDashboard';
import DashboardInsights from './DashboardInsights';
import { useTranslation } from 'react-i18next';
import { fetchPreferences, orderFor, ORDER_ALL } from '../utils/preferences';
import { 
    FaTicketAlt, 
    FaBoxOpen, 
    FaShoppingCart, 
    FaChartLine,
    FaExternalLinkAlt,
    FaExclamationCircle,
    FaUsers,
    FaMoneyBillWave,
    FaExclamationTriangle,
    FaBox,
    FaShieldAlt,
    FaCheckCircle,
    FaCircle
} from 'react-icons/fa';

const EDN_TICKET_TOPICS = [
    { id: 22, topic_full: "Complément d'adresse" },
    { id: 23, topic_full: "Retard de livraison / demande de localisation" },
    { id: 24, topic_full: "Demande de retour colis" },
    { id: 25, topic_full: "Colis bloqué en douane" },
    { id: 30, topic_full: "Demande de preuve de livraison / Confirmation de livraison" },
    { id: 31, topic_full: "Demande de preuve de livraison / Obtenir une preuve de livraison" },
    { id: 32, topic_full: "Marchandise manquante dans le colis" },
    { id: 33, topic_full: "Marchandise endommagée" },
    { id: 34, topic_full: "Contestation de livraison" },
    { id: 35, topic_full: "Retour non reçu" },
    { id: 36, topic_full: "Facturation et paiement" },
    { id: 37, topic_full: "Autre" },
    { id: 38, topic_full: "Colis en retard donnant lieu à une indemnisation" },
    { id: 39, topic_full: "Colis déposé au mauvais point relais" },
    { id: 40, topic_full: "Multi colis non respecté" },
    { id: 41, topic_full: "Problème d'enlèvement de votre marchandise" },
    { id: 42, topic_full: "Colis en souffrance" },
];

const Dashboard = () => {
    const [user, setUser] = useState(undefined);
    const [tickets, setTickets] = useState([]);
    const [assignedTickets, setAssignedTickets] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [assignedCurrentPage, setAssignedCurrentPage] = useState(1);
    const [paginationInfo, setPaginationInfo] = useState(null);
    const [assignedPaginationInfo, setAssignedPaginationInfo] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [notificationState, setNotificationState] = useState({ message: '', success: true, visible: false });
    // Header period selector: sizes the KPI windows of DashboardInsights.
    const [windowDays, setWindowDays] = useState(7);
    // Active action-strip chip (backend attention_reason filter on the lists).
    const [attentionReason, setAttentionReason] = useState(null);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const accountMenuRef = useRef(null);

    const [orders, setOrders] = useState([]);
    const [returns, setReturns] = useState([]);

    useEffect(() => {
        if (!accountMenuOpen) return undefined;
        const handleClickOutside = (event) => {
            if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
                setAccountMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [accountMenuOpen]);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    // Server-side sort of the ticket tables (KI-111): the list endpoints
    // whitelist these values, so an unknown URL value falls back to the default.
    const TICKET_SORT_FIELDS = ['updated_at', 'created_at', 'sentiment_score'];
    const readSortFromUrl = () => {
        const sortField = searchParams.get('sortField');
        const sortOrder = searchParams.get('sortOrder');
        return {
            sort_by: TICKET_SORT_FIELDS.includes(sortField) ? sortField : 'updated_at',
            order: sortOrder === 'asc' ? 'asc' : 'desc'
        };
    };
    const appendSortParams = (queryParams, filters) => {
        const sortBy = TICKET_SORT_FIELDS.includes(filters?.sort_by) ? filters.sort_by : 'updated_at';
        const order = filters?.order === 'asc' ? 'asc' : 'desc';
        queryParams.append('sort_by', sortBy);
        queryParams.append('order', order);
    };
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    // KI-242: the page renders as soon as the user is known; the ticket list
    // shows its own placeholder until its first answer.
    const [ticketsLoading, setTicketsLoading] = useState(true);
    const [shopStats, setShopStats] = useState(null);
    const [isShopConfigured, setIsShopConfigured] = useState(true);

    // Brand-related state
    const [brands, setBrands] = useState([]);
    // Brand ids are Mongo ObjectId STRINGS (BrandResponse.id) — never parseInt.
    const [selectedBrand, setSelectedBrand] = useState(searchParams.get('selectedBrand') || null);

    // Add state for current filters - updated structure
    const [currentFilters, setCurrentFilters] = useState({
        topics: [], // Will be set based on user type
        separate_assigned: true // New parameter for backend
    });

    // Add state to track current view mode
    const [currentViewMode, setCurrentViewMode] = useState('unassigned'); // 'unassigned' | 'assigned'

    // State for the new ticket creation overlay
    const [isCreateTicketOverlayOpen, setIsCreateTicketOverlayOpen] = useState(false);
    const [newTicketData, setNewTicketData] = useState({ tracking: '', message: '', ticket_topic_id: '' });
    const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
    const [submitTicketError, setSubmitTicketError] = useState(null);
    const [submitTicketSuccess, setSubmitTicketSuccess] = useState(null);

    const isSearchActiveRef = useRef(false);
    const currentFiltersRef = useRef(currentFilters);
    const currentPageRef = useRef(currentPage);
    const lastTicketsRefreshRef = useRef(0);
    const pendingRefreshTimeoutRef = useRef(null);
    // 10 s: every tenant event (incl. widget messages) schedules a list refresh;
    // it is the heaviest endpoint (p95 ~0.5 s), so coalesce a little more.
    const TICKETS_REFRESH_MIN_INTERVAL_MS = 10000;

    // Websocket refresh messages arrive for every ticket event; without a
    // debounce each one re-ran the full tickets fetch against the backend.
    const scheduleTicketsRefresh = () => {
        const runRefresh = () => {
            const token = localStorage.getItem('access_token');
            if (!token) return;
            lastTicketsRefreshRef.current = Date.now();
            fetchTickets(token, currentPageRef.current, currentFiltersRef.current);
        };

        const elapsed = Date.now() - lastTicketsRefreshRef.current;
        if (elapsed >= TICKETS_REFRESH_MIN_INTERVAL_MS) {
            runRefresh();
            return;
        }
        if (pendingRefreshTimeoutRef.current) return; // trailing refresh already scheduled
        pendingRefreshTimeoutRef.current = setTimeout(() => {
            pendingRefreshTimeoutRef.current = null;
            runRefresh();
        }, TICKETS_REFRESH_MIN_INTERVAL_MS - elapsed);
    };

    useEffect(() => {
        isSearchActiveRef.current = isSearchActive;
    }, [isSearchActive]);

    useEffect(() => {
        currentFiltersRef.current = currentFilters;
    }, [currentFilters]);

    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    const connectWebSocket = () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        // Check token expiration before connecting
        try {
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            if (tokenPayload.exp && tokenPayload.exp * 1000 < Date.now()) {
                console.log('Token has expired, not connecting WebSocket');
                // Clear session and redirect to login if needed
                localStorage.removeItem('access_token');
                navigate('/');
                return;
            }
        } catch (error) {
            console.error('Error parsing token:', error);
            return;
        }

        // Close any previous socket before opening a new one: a leaked
        // handler that keeps reconnecting stacks live connections server-side.
        if (wsRef.current) {
            wsRef.current.onclose = null;
            wsRef.current.close();
        }

        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const ws = new WebSocket(
            `${wsProtocol}//${window.location.host}/api/ws?token=${encodeURIComponent(token)}`
        );
        wsRef.current = ws;

        ws.onopen = (event) => {
            console.log('WebSocket connection established:', event);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        ws.onclose = (event) => {
            // Reconnect only if this socket is still the active one — after
            // unmount or replacement the ref no longer points at it.
            if (wsRef.current !== ws) return;
            console.log('WebSocket connection closed:', event);
            console.log('Close code:', event.code);
            console.log('Close reason:', event.reason);
            
            // Check if closure was due to authentication error (typical codes for auth issues)
            if (event.code === 1008 || event.code === 4000 || event.code === 4001 || 
                event.reason?.toLowerCase().includes('token') || 
                event.reason?.toLowerCase().includes('auth')) {
                console.log('WebSocket closed due to authentication issue, not reconnecting');
                // Optionally redirect to login
                localStorage.removeItem('access_token');
                navigate('/');
                return;
            }

            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        };

        ws.onmessage = (event) => {
            console.log('WebSocket message received:', event.data);
            if (event.data === 'ping') {
                ws.send('pong');
            } else {
                // Try to parse as JSON for presence updates
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'ticket_presence_update') {
                        handlePresenceUpdate(data);
                        return;
                    }
                } catch (e) {
                    // Not JSON, treat as regular refresh message
                }
                
                // Use ref instead of stale closure variable
                if (!isSearchActiveRef.current) {
                    scheduleTicketsRefresh();
                }
            }
        };
    };

    const handlePresenceUpdate = (data) => {
        const { ticket_id, action, viewer } = data;
        
        // Update unassigned tickets state
        setTickets(prevTickets => prevTickets.map(ticket => {
            if (ticket._id === ticket_id) {
                let updatedViewers = [...(ticket.presence_viewers || [])];
                if (action === 'join') {
                    // Add or update viewer
                    const existingIndex = updatedViewers.findIndex(v => v.viewer_id === viewer.viewer_id);
                    if (existingIndex >= 0) {
                        updatedViewers[existingIndex] = viewer;
                    } else {
                        updatedViewers.push(viewer);
                    }
                } else if (action === 'leave') {
                    // Remove viewer
                    updatedViewers = updatedViewers.filter(v => v.viewer_id !== viewer.viewer_id);
                }
                return { ...ticket, presence_viewers: updatedViewers };
            }
            return ticket;
        }));
        
        // Also update assigned tickets state
        setAssignedTickets(prevTickets => prevTickets.map(ticket => {
            if (ticket._id === ticket_id) {
                let updatedViewers = [...(ticket.presence_viewers || [])];
                if (action === 'join') {
                    const existingIndex = updatedViewers.findIndex(v => v.viewer_id === viewer.viewer_id);
                    if (existingIndex >= 0) {
                        updatedViewers[existingIndex] = viewer;
                    } else {
                        updatedViewers.push(viewer);
                    }
                } else if (action === 'leave') {
                    updatedViewers = updatedViewers.filter(v => v.viewer_id !== viewer.viewer_id);
                }
                return { ...ticket, presence_viewers: updatedViewers };
            }
            return ticket;
        }));
    };


    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchAllData = async () => {
            try {
                setIsLoading(true);
                
                const userData = await fetchUserData(token);
                // The person's own defaults (28/10): the view the dashboard
                // opens on and the order of the list, unless the URL already
                // says (a link, the back button). Applied through the URL so
                // the table restores them like a deep link.
                const initialSort = readSortFromUrl();
                const preferences = await fetchPreferences();
                const defaults = {};
                if (!searchParams.get('activeTab') && preferences.default_view_id) {
                    defaults.activeTab = `view:${preferences.default_view_id}`;
                }
                if (!searchParams.get('sortOrder')) {
                    initialSort.sort_by = 'updated_at';
                    initialSort.order = orderFor(preferences, searchParams.get('statusFilter') || ORDER_ALL);
                    defaults.sortField = initialSort.sort_by;
                    defaults.sortOrder = initialSort.order;
                }
                if (Object.keys(defaults).length > 0) {
                    const params = new URLSearchParams(searchParams);
                    Object.entries(defaults).forEach(([key, value]) => params.set(key, value));
                    setSearchParams(params, { replace: true });
                }
                // KI-242: render the dashboard shell now — the statistics
                // (DashboardInsights) load in parallel with the ticket list
                // instead of after it.
                setIsLoading(false);
                
                // Set initial filters based on user type
                const initialFilters = {
                    topics: [],
                    separate_assigned: true,
                    ...initialSort
                };
                setCurrentFilters(initialFilters);
                
                const promises = [
                    fetchTickets(token, 1, initialFilters) // Use initial filters based on user type
                ];
                
                // Fetch brands if user has_brand is true
                if (userData?.has_brand) {
                    console.log('🚀 User has brands, fetching brand list...');
                    promises.push(fetchBrands(token));
                } else {
                    console.log('🚫 User does not have brands (has_brand:', userData?.has_brand, ')');
                }
                
                // Legacy e-commerce widgets: only for the old shop roles.
                // Admins are logisticians and never need these calls.
                if (['eshop', 'seller', 'experience'].includes(userData?.role)) {
                    promises.push(
                        fetchOrders(token),
                        fetchReturns(token),
                        fetchShopStatistics(token)
                    );
                }
                
                await Promise.all(promises);

            } catch (error) {
                console.error('Error loading dashboard data:', error);
            } finally {
                setIsLoading(false);
                setTicketsLoading(false);
            }
        };

        fetchAllData();
        connectWebSocket();

        const pingInterval = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send('ping');
            }
        }, 30000);

        return () => {
            clearInterval(pingInterval);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (pendingRefreshTimeoutRef.current) {
                clearTimeout(pendingRefreshTimeoutRef.current);
            }
            if (wsRef.current) {
                // Null the ref before the async onclose fires so its
                // reconnect guard sees the socket as retired.
                const ws = wsRef.current;
                wsRef.current = null;
                ws.close();
            }
        };
    }, [navigate]);

    useEffect(() => {
        if (location.state?.notification) {
            const { message, success } = location.state.notification;
            setNotificationState({ message, success, visible: true });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    useEffect(() => {
        if (notificationState.visible) {
            const timeoutId = setTimeout(() => {
                setNotificationState(prev => ({ ...prev, visible: false }));
            }, 4000);

            return () => clearTimeout(timeoutId);
        }
    }, [notificationState.visible]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('👤 User data received:', response.data);
            console.log('🏷️ User has_brand:', response.data?.has_brand);
            setUser(response.data);
            if (response.data?._id) {
                localStorage.setItem('user_id', response.data._id);
            }
            return response.data;
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            return null;
        }
    };

    const fetchTickets = async (token, page = 1, filters = null, assignedPage = 1) => {
        try {
            const ticketsPerPage = 100;
            
            // Use provided filters or current filters
            const activeFilters = filters || currentFilters;
            
            // Build query parameters
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ticketsPerPage.toString(),
            });

            // Only add separated parameters for non-employee users
            if (activeFilters.separate_assigned) {
                queryParams.append('assigned_page', assignedPage.toString());
                queryParams.append('assigned_limit', ticketsPerPage.toString());
                queryParams.append('separate_assigned', 'true');
            }

            // Add topics to query params if they exist
            if (activeFilters.topics && activeFilters.topics.length > 0) {
                activeFilters.topics.forEach(topic => {
                    queryParams.append('topics', topic);
                });
            }

            if (activeFilters.viewId) {
                queryParams.append('view_id', activeFilters.viewId);
            }

            // Add VIP filter if enabled
            if (activeFilters.is_vip_only) {
                queryParams.append('is_vip_only', 'true');
            }

            // Action-strip chip filter (see DashboardInsights): the snapshot
            // makes the list match the counter that was clicked (KI-242).
            if (activeFilters.attention_reason) {
                queryParams.append('attention_reason', activeFilters.attention_reason);
                if (activeFilters.attention_snapshot) {
                    queryParams.append('attention_snapshot', activeFilters.attention_snapshot);
                }
            }

            appendSortParams(queryParams, activeFilters);

            const response = await axios.get(`/api/tickets/tickets?${queryParams}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                if (activeFilters.separate_assigned) {
                    // Handle separated response for non-employee users
                    setTickets(response.data.unassigned_tickets || []);
                    setAssignedTickets(response.data.assigned_tickets || []);
                    setPaginationInfo(response.data.unassigned_pagination);
                    setAssignedPaginationInfo(response.data.assigned_pagination);
                } else {
                    // Handle simple response for employee users
                    setTickets(response.data.tickets || []);
                    setAssignedTickets([]);
                    setPaginationInfo(response.data.pagination);
                    setAssignedPaginationInfo(null);
                }
                
                setCurrentPage(page);
                setAssignedCurrentPage(assignedPage);
                setIsInitialLoad(false);
            } else {
                setTickets([]);
                setAssignedTickets([]);
                setPaginationInfo(null);
                setAssignedPaginationInfo(null);
            }
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
            setTickets([]);
            setAssignedTickets([]);
            setPaginationInfo(null);
            setAssignedPaginationInfo(null);
        }
    };

    const fetchMyAssignedTickets = async (token, page = 1, filters = null) => {
        try {
            const ticketsPerPage = 100;
            const activeFilters = filters || currentFilters;
            
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ticketsPerPage.toString(),
            });

            // Add topics to query params if they exist
            if (activeFilters.topics && activeFilters.topics.length > 0) {
                activeFilters.topics.forEach(topic => {
                    queryParams.append('topics', topic);
                });
            }

            if (activeFilters.viewId) {
                queryParams.append('view_id', activeFilters.viewId);
            }

            // Add VIP filter if enabled
            if (activeFilters.is_vip_only) {
                queryParams.append('is_vip_only', 'true');
            }

            appendSortParams(queryParams, activeFilters);

            const response = await axios.get(`/api/tickets/tickets/my-assigned?${queryParams}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                // my-assigned endpoint returns tickets in assignedTickets slot
                setTickets([]);
                setAssignedTickets(response.data.tickets || []);
                setPaginationInfo(null);
                setAssignedPaginationInfo(response.data.pagination);
                setAssignedCurrentPage(page);
                setIsInitialLoad(false);
            } else {
                setTickets([]);
                setAssignedTickets([]);
                setPaginationInfo(null);
                setAssignedPaginationInfo(null);
            }
        } catch (error) {
            console.error('Failed to fetch my assigned tickets:', error);
            setTickets([]);
            setAssignedTickets([]);
            setPaginationInfo(null);
            setAssignedPaginationInfo(null);
        }
    };

    // Transform order data from new API format to match component expectations (similar to OrdersList.js)
    const transformOrderData = (apiOrder) => {
        // Log the raw API order data
        console.log('🔍 Raw API Order Data:', JSON.stringify(apiOrder, null, 2));
        console.log('🎯 Risk Score in API Order:', apiOrder.risk_prediction?.risk_score);
        
        const transformedOrder = {
            id: apiOrder._id,
            order_number: apiOrder.order_number,
            created_at: apiOrder.created_at,
            customer: {
                // Fix: Use billing address name instead of order name
                name: apiOrder.billing_address?.name || apiOrder.shipping_address?.name || null,
                email: apiOrder.customer_email || null
            },
            line_items: apiOrder.line_items || [],
            total_price: apiOrder.order_total?.toString() || '0.00',
            financial_status: apiOrder.financial_status,
            fulfillment_status: apiOrder.fulfillment_status,
            // Replace source_marketplace with shipping_country
            shipping_country: apiOrder.shipping_address?.country || apiOrder.billing_address?.country || null,
            tracking: apiOrder.tracking || [],
            risk_prediction_score: apiOrder.risk_prediction?.risk_score || null,
            // Include any other fields that might be needed
            ...apiOrder
        };
        
        // Log the transformed order data
        console.log('✅ Transformed Order Data:', JSON.stringify(transformedOrder, null, 2));
        console.log('🎯 Risk Score in Transformed Order:', transformedOrder.risk_prediction_score);
        
        return transformedOrder;
    };

    const fetchOrders = async (token) => {
        try {
            const response = await axios.get('/api/orders_management/orders', {
                headers: { Authorization: `Bearer ${token}` },
                params: { limit: 5 }
            });
            if (response.data.status === "unconfigured") {
                setOrders([]);
            } else {
                // Transform the orders to match component expectations
                const transformedOrders = (response.data.orders || []).map(transformOrderData);
                setOrders(transformedOrders);
            }
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            setOrders([]);
        } 
    };

    const fetchReturns = async (token) => {
        try {
            const response = await axios.get('/api/webhook/returns', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const returnsData = response.data.returns || [];
            setReturns(returnsData.slice(0, 5));
        } catch (error) {
            console.error('Failed to fetch returns:', error);
            setReturns([]);
        }
    };

    const fetchShopStatistics = async (token) => {
        try {
            const response = await axios.get('/api/shopify/get_shop_statistics', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.status === "unconfigured") {
                setIsShopConfigured(false);
                setShopStats(null);
            } else {
                setIsShopConfigured(true);
                setShopStats(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch shop statistics:', error);
            setShopStats(null);
            setIsShopConfigured(false);
        }
    };

    const fetchBrands = async (token) => {
        console.log('🔍 fetchBrands called');
        try {
            const response = await axios.get('/api/brands/list', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('✅ Brand API response:', response.data);
            setBrands(response.data || []);
            // Set the first brand as default selected if brands exist
            if (response.data && response.data.length > 0) {
                console.log('🎯 Setting selected brand to:', response.data[0].id);
                setSelectedBrand(response.data[0].id);
            } else {
                console.log('❌ No brands found in response');
            }
        } catch (error) {
            console.error('❌ Failed to fetch brands:', error);
            setBrands([]);
        }
    };

    const handleTicketClick = (ticketId) => {
        // Store search params and navigate immediately
        const currentSearch = location.search || '';
        if (currentSearch) {
            sessionStorage.setItem('una_dashboard_search', currentSearch);
        } else {
            sessionStorage.removeItem('una_dashboard_search');
        }
        navigate(`/tickets/${ticketId}/messages`, {
            state: { fromDashboardSearch: location.search }
        });
    };

    // Handlers for the create ticket overlay
    const handleOpenCreateTicketOverlay = () => {
        setIsCreateTicketOverlayOpen(true);
        setSubmitTicketError(null);
        setSubmitTicketSuccess(null);
        setNewTicketData({ tracking: '', message: '', ticket_topic_id: '' });
    };

    const handleCloseCreateTicketOverlay = () => {
        setIsCreateTicketOverlayOpen(false);
    };

    const handleNewTicketInputChange = (e) => {
        const { name, value } = e.target;
        setNewTicketData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitNewTicket = async (e) => {
        e.preventDefault();
        setIsSubmittingTicket(true);
        setSubmitTicketError(null);
        setSubmitTicketSuccess(null);
        const token = localStorage.getItem('access_token');

        if (!newTicketData.ticket_topic_id) {
            setSubmitTicketError(t('ticketTopicRequired', 'Please select a ticket topic.'));
            setIsSubmittingTicket(false);
            return;
        }

        const payload = {
            ...newTicketData,
            ticket_topic_id: parseInt(newTicketData.ticket_topic_id, 10)
        };

        try {
            const response = await axios.post('/api/tickets/tickets/forward_to_envoidunet_create', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSubmitTicketSuccess(t('ticketCreatedSuccessfully', 'Ticket created successfully!') + (response.data?.ticket_id ? ` ID: ${response.data.ticket_id}` : ''));
            setNewTicketData({ tracking: '', message: '', ticket_topic_id: '' });
            setTimeout(() => {
                handleCloseCreateTicketOverlay();
            }, 2500);
        } catch (error) {
            console.error('Failed to create EDN ticket:', error);
            setSubmitTicketError(error.response?.data?.detail || t('failedToCreateEdnTicket', 'Failed to create ticket. Please try again.'));
        } finally {
            setIsSubmittingTicket(false);
        }
    };

    const handleBrandChange = (brandId) => {
        console.log('🔄 Brand changed to:', brandId);
        setSelectedBrand(brandId);
        
        // Update URL parameter for brand selection
        const newParams = new URLSearchParams(searchParams);
        if (brandId) {
            newParams.set('selectedBrand', brandId.toString());
        } else {
            newParams.delete('selectedBrand');
        }
        setSearchParams(newParams, { replace: true });
    };

    const handleFilterChange = (newFilters) => {
        // Tab/view payloads carry neither the sort nor the attention chip:
        // keep both in effect.
        const updatedFilters = {
            sort_by: currentFilters.sort_by,
            order: currentFilters.order,
            attention_reason: currentFilters.attention_reason || null,
            attention_snapshot: currentFilters.attention_snapshot || null,
            ...newFilters
        };
        setCurrentFilters(updatedFilters);
        
        // Update view mode based on filter change
        if (newFilters.viewMode !== undefined) {
            setCurrentViewMode(newFilters.viewMode);
        }
        
        const token = localStorage.getItem('access_token');
        if (token) {
            // Use dedicated endpoint for my_assigned view mode
            if (newFilters.viewMode === 'my_assigned') {
                fetchMyAssignedTickets(token, 1, updatedFilters);
            } else {
                fetchTickets(token, 1, updatedFilters, 1); // Reset both pages when filtering
            }
        }
    };

    // Action-strip chip: filter the queue on one attention reason (toggle),
    // as counted by the insights snapshot the chip belongs to.
    const handleAttentionReasonChange = (reason, snapshotId = null) => {
        setAttentionReason(reason);
        const updatedFilters = {
            ...currentFilters,
            attention_reason: reason,
            attention_snapshot: reason ? snapshotId : null
        };
        setCurrentFilters(updatedFilters);
        const token = localStorage.getItem('access_token');
        if (!token) return;
        if (currentViewMode === 'my_assigned') {
            fetchMyAssignedTickets(token, 1, updatedFilters);
        } else {
            fetchTickets(token, 1, updatedFilters, 1);
        }
    };

    // Refetch the current lists in place (after a bulk assign/close).
    const refreshTickets = () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        if (currentViewMode === 'my_assigned') {
            fetchMyAssignedTickets(token, assignedCurrentPage, currentFilters);
        } else {
            fetchTickets(token, currentPage, currentFilters, assignedCurrentPage);
        }
    };

    const notify = (message, success = true) => {
        setNotificationState({ message, success, visible: true });
    };

    // Column-header sort (KI-111): re-query page 1 with the new order instead
    // of re-sorting the 100 tickets already loaded.
    const handleSortChange = ({ sort_by, order }) => {
        const updatedFilters = { ...currentFilters, sort_by, order };
        setCurrentFilters(updatedFilters);

        const token = localStorage.getItem('access_token');
        if (!token) {
            return;
        }
        if (currentViewMode === 'my_assigned') {
            fetchMyAssignedTickets(token, 1, updatedFilters);
        } else {
            fetchTickets(token, 1, updatedFilters, 1);
        }
    };

    const handlePageChange = (newUnassignedPage, newAssignedPage) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            return;
        }

        const hasUnassignedChange = newUnassignedPage !== null && newUnassignedPage !== undefined;
        const hasAssignedChange = newAssignedPage !== null && newAssignedPage !== undefined;

        if (!hasUnassignedChange && !hasAssignedChange) {
            return;
        }

        const targetUnassigned = hasUnassignedChange ? newUnassignedPage : currentPage;
        const targetAssigned = hasAssignedChange ? newAssignedPage : assignedCurrentPage;

        if (currentFilters.viewId) {
            fetchTickets(token, targetUnassigned, currentFilters, targetAssigned);
            return;
        }

        // Handle my_assigned view mode with dedicated endpoint
        if (currentViewMode === 'my_assigned') {
            if (hasAssignedChange && newAssignedPage > 0 && (!assignedPaginationInfo?.total_pages || newAssignedPage <= assignedPaginationInfo.total_pages)) {
                fetchMyAssignedTickets(token, newAssignedPage, currentFilters);
            }
            return;
        }

        if (currentViewMode === 'assigned') {
            if (hasAssignedChange && newAssignedPage > 0 && (!assignedPaginationInfo?.total_pages || newAssignedPage <= assignedPaginationInfo.total_pages)) {
                fetchTickets(token, currentPage, currentFilters, newAssignedPage);
            }
            return;
        }

        if (hasUnassignedChange && newUnassignedPage > 0 && (!paginationInfo?.total_pages || newUnassignedPage <= paginationInfo.total_pages)) {
            fetchTickets(token, newUnassignedPage, currentFilters, assignedCurrentPage);
        }
    };

    // Add risk score helper functions (similar to OrdersList.js)
    const getRiskScoreIcon = (score) => {
        console.log('🎯 getRiskScoreIcon called with:', { score, type: typeof score });
        
        if (score === null || score === undefined) return <FaCircle className="risk-icon risk-unknown" />;
        
        if (score >= 0.8) return <FaExclamationTriangle className="risk-icon risk-high" />;
        if (score >= 0.6) return <FaExclamationCircle className="risk-icon risk-medium" />;
        if (score >= 0.4) return <FaShieldAlt className="risk-icon risk-low" />;
        return <FaCheckCircle className="risk-icon risk-safe" />;
    };

    const getRiskScoreClass = (score) => {
        console.log('🎯 getRiskScoreClass called with:', { score, type: typeof score });
        
        if (score === null || score === undefined) return 'una-risk-unknown';
        
        if (score >= 0.8) return 'una-risk-high';
        if (score >= 0.6) return 'una-risk-medium';
        if (score >= 0.4) return 'una-risk-low';
        return 'una-risk-safe';
    };

    const formatRiskScore = (score) => {
        console.log('🎯 formatRiskScore called with:', { score, type: typeof score });
        
        if (score === null || score === undefined) return '-';
        return (score * 100).toFixed(0) + '%';
    };

    // Add function to get risk score text for badges
    const getRiskScoreText = (score) => {
        console.log('🎯 getRiskScoreText called with:', { score, type: typeof score });
        
        if (score === null || score === undefined) return '-';
        
        if (score >= 0.8) return 'High';
        if (score >= 0.6) return 'Medium';
        if (score >= 0.4) return 'Low';
        return 'Safe';
    };

    if (isLoading) {
        return (
            <div className="una-dashboard-loading-screen">
                <div className="una-loading-content">
                    <div className="una-loading-logo">
                        <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
                    </div>
                    <h2>{t('loadingDashboard')}</h2>
                    <div className="una-loading-progress">
                        <div className="una-progress-bar">
                            <div className="una-progress-glow"></div>
                        </div>
                    </div>
                    <div className="una-loading-metrics">
                        <div className="una-metric-item">
                            <FaTicketAlt />
                            <span>{t('loadingTickets')}</span>
                        </div>
                        <div className="una-metric-item">
                            <FaShoppingCart />
                            <span>{t('loadingOrders')}</span>
                        </div>
                        <div className="una-metric-item">
                            <FaChartLine />
                            <span>{t('loadingStats')}</span>
                        </div>
                        <div className="una-metric-item">
                            <FaBoxOpen />
                            <span>{t('loadingReturns')}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="una-dashboard-container">
            {notificationState.visible && (
                <div className={`una-dashboard-notification ${notificationState.success ? 'success' : 'error'}`}>
                    <div className="notification-icon">
                        {notificationState.success ? <FaCheckCircle /> : <FaExclamationCircle />}
                    </div>
                    <div className="notification-content">
                        <span>{notificationState.message}</span>
                    </div>
                    <button
                        type="button"
                        className="notification-close"
                        onClick={() => setNotificationState(prev => ({ ...prev, visible: false }))}
                    >
                        {t('dashboard_dismissNotification', 'Dismiss')}
                    </button>
                </div>
            )}
            <div className="una-dashboard-main">
                {(user?.edn_api_account || user?.role === 'admin' || (user?.role === 'employee' && user?.admin_role === 'admin')) ? (
                    <>
                    <div className="una-page-header">
                        <h1 className="una-page-title">{t('dashboard', 'Tableau de bord')}</h1>
                        <div className="una-page-header-right">
                            {brands.length > 0 && (
                                <label className="una-scope-select">
                                    <span>{t('headerShop', 'Boutique')}&nbsp;:</span>
                                    <select
                                        value={selectedBrand ?? ''}
                                        onChange={(e) => handleBrandChange(e.target.value || null)}
                                    >
                                        {brands.map((brand) => (
                                            <option key={brand.id} value={brand.id}>{brand.name}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <select
                                className="una-period-select"
                                value={windowDays}
                                onChange={(e) => setWindowDays(Number(e.target.value))}
                                aria-label={t('headerPeriod7', '7 derniers jours')}
                            >
                                <option value={7}>{t('headerPeriod7', '7 derniers jours')}</option>
                                <option value={30}>{t('headerPeriod30', '30 derniers jours')}</option>
                            </select>
                            <div className="una-header-account" ref={accountMenuRef}>
                                <button
                                    type="button"
                                    className="una-header-avatar"
                                    onClick={() => setAccountMenuOpen((prev) => !prev)}
                                    aria-haspopup="menu"
                                    aria-expanded={accountMenuOpen}
                                    title={user?.email || ''}
                                >
                                    {(user?.shop_name || user?.email || 'U').slice(0, 2).toUpperCase()}
                                </button>
                                {accountMenuOpen && (
                                    <div className="una-header-account-menu" role="menu">
                                        <button type="button" role="menuitem" onClick={() => { setAccountMenuOpen(false); navigate('/parameters'); }}>
                                            {t('sidebarNav.userParameters', 'Compte & préférences')}
                                        </button>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            className="danger"
                                            onClick={() => {
                                                localStorage.removeItem('access_token');
                                                localStorage.removeItem('userData');
                                                navigate('/');
                                            }}
                                        >
                                            {t('logout', 'Déconnexion')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <DashboardInsights
                        windowDays={windowDays}
                        activeReason={attentionReason}
                        onReasonSelect={handleAttentionReasonChange}
                    />
                    <TicketTableDashboard
                        tickets={tickets} 
                        assignedTickets={assignedTickets}
                        user={user}
                        onTicketClick={handleTicketClick}
                        hasFaAccount={user?.fa_account}
                        onCreateTicket={handleOpenCreateTicketOverlay}
                        pagination={paginationInfo}
                        assignedPagination={assignedPaginationInfo}
                        onPageChange={handlePageChange}
                        onFilterChange={handleFilterChange}
                        onSortChange={handleSortChange}
                        currentViewMode={currentViewMode}
                        onSearchStateChange={setIsSearchActive}
                        selectedBrand={selectedBrand}
                        attentionReason={attentionReason}
                        onAttentionReasonChange={handleAttentionReasonChange}
                        isLoading={ticketsLoading}
                        onRefresh={refreshTickets}
                        onNotify={notify}
                    />
                    </>
                ) : !isShopConfigured ? (
                    <div className="una-unconfigured-shop">
                        <div className="una-welcome-message">
                            <FaShoppingCart className="una-welcome-icon" />
                            <h2>{t('welcomeToUnanimity')}</h2>
                            <p>{t('configureShopMessage')}</p>
                            <button 
                                className="una-primary-button"
                                onClick={() => navigate('/parameters')}
                            >
                                {t('configureShop')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="una-business-dashboard">
                        {/* Shop Statistics Overview */}
                        {shopStats && (
                            <div className="una-store-overview">
                                <div className="dashboard-stats-grid">
                                    <div className="una-stat-card revenue">
                                        <FaMoneyBillWave />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.totalRevenue')}</h3>
                                            <p>${shopStats.orders?.total_revenue?.toFixed(2) || '0.00'}</p>
                                            <span>{t('shopStats.avgOrder')}: ${shopStats.orders?.average_order_value?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    </div>

                                    <div className="una-stat-card orders">
                                        <FaShoppingCart />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.totalOrders')}</h3>
                                            <p>{shopStats.orders?.total_orders || 0}</p>
                                            <span>{t('shopStats.avgItems')}: {shopStats.sales_analysis?.average_items_per_order?.toFixed(1) || '0.0'}</span>
                                        </div>
                                    </div>

                                    <div className="una-stat-card customers">
                                        <FaUsers />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.customers')}</h3>
                                            <p>{shopStats.customers?.total_customers || 0}</p>
                                            <span>{t('shopStats.newCustomers')}: {shopStats.customers?.new_customers_30d || 0}</span>
                                        </div>
                                    </div>

                                    <div className="una-stat-card products">
                                        <FaBox />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.products')}</h3>
                                            <p>{shopStats.products?.total_products || 0}</p>
                                            <span>{t('shopStats.variants')}: {shopStats.products?.total_variants || 0}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Alerts */}
                                <div className="una-alerts-section">
                                    {shopStats.products?.out_of_stock_count > 0 && (
                                        <div className="una-alert out-of-stock">
                                            <FaExclamationTriangle />
                                            <span>{shopStats.products.out_of_stock_count} {t('shopStats.outOfStock')}</span>
                                        </div>
                                    )}
                                    {shopStats.products?.low_stock_count > 0 && (
                                        <div className="una-alert low-stock">
                                            <FaExclamationTriangle />
                                            <span>{shopStats.products.low_stock_count} {t('shopStats.lowStock')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Main dashboard grid with key information - recent orders + returns */}
                        <div className="una-dashboard-grid">
                            <div className="una-dashboard-card una-full-content">
                                <div className="una-card-header">
                                    <FaShoppingCart className="orders-icon" />
                                    <h3>{t('recentOrders')}</h3>
                                    <button onClick={() => navigate('/orders')} className="una-view-all">
                                        <FaExternalLinkAlt />
                                    </button>
                                </div>
                                <div className="una-card-content">
                                    {orders.slice(0, 5).map(order => (
                                        <div key={order.id} className="una-compact-item" onClick={() => navigate(`/order/${order.id}`)}>
                                            <div className="una-compact-info">
                                                <span className="una-primary-text">#{order.order_number}</span>
                                                <span className="una-secondary-text">{order.customer?.name}</span>
                                            </div>
                                            <div className="una-compact-details">
                                                {/* Add risk score display */}
                                                {(order.risk_prediction_score !== null && order.risk_prediction_score !== undefined) && (
                                                    <div className={`una-risk-score-compact ${getRiskScoreClass(order.risk_prediction_score)}`}>
                                                        {getRiskScoreIcon(order.risk_prediction_score)}
                                                        <span>{getRiskScoreText(order.risk_prediction_score)}</span>
                                                    </div>
                                                )}
                                                <span className="una-price-tag">${order.total_price}</span>
                                                <span className={`una-status-badge-dashboard ${order.financial_status?.toLowerCase()}`}>
                                                    {order.financial_status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="una-dashboard-card una-full-content">
                                <div className="una-card-header">
                                    <FaBoxOpen className="returns-icon" />
                                    <h3>{t('recentReturns')}</h3>
                                    <button onClick={() => navigate('/returns')} className="una-view-all">
                                        <FaExternalLinkAlt />
                                    </button>
                                </div>
                                <div className="una-card-content">
                                    {returns.slice(0, 5).map(returnItem => (
                                        <div 
                                            key={returnItem._id} 
                                            className="una-compact-item"
                                            onClick={() => navigate(`/return/${returnItem._id}`)}
                                        >
                                            <div className="una-compact-info">
                                                <span className="una-primary-text">{returnItem.items?.[0]?.title || 'No title'}</span>
                                                <span className="una-secondary-text">#{returnItem.order_id}</span>
                                            </div>
                                            <div className="una-compact-details">
                                                <span className="una-price-tag">
                                                    €{(returnItem.items?.[0]?.price !== undefined ? 
                                                        Number(returnItem.items[0].price).toFixed(2) : 
                                                        '0.00')}
                                                </span>
                                                <span className={`una-status-badge-dashboard ${returnItem.status.replace('_', ' ').replace(' ', '-').toLowerCase()}`}>
                                                    {returnItem.status.replace('_', ' ')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Ticket table shown after the main dashboard */}
                        <div className="una-tickets-section">
                            <div className="una-section-header">
                                <FaTicketAlt className="tickets-icon" />
                                <h2>{t('supportTickets')}</h2>
                            </div>
                            <TicketTableDashboard 
                                tickets={tickets} 
                                assignedTickets={assignedTickets}
                                user={user}
                                onTicketClick={handleTicketClick}
                                compact={true}
                                hasFaAccount={user?.fa_account}
                                onCreateTicket={handleOpenCreateTicketOverlay}
                                brands={brands}
                                selectedBrand={selectedBrand}
                                onBrandChange={handleBrandChange}
                                pagination={paginationInfo}
                                assignedPagination={assignedPaginationInfo}
                                onPageChange={handlePageChange}
                                onFilterChange={handleFilterChange}
                                onSortChange={handleSortChange}
                                currentViewMode={currentViewMode}
                                onSearchStateChange={setIsSearchActive}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Overlay for creating EDN ticket */}
            {isCreateTicketOverlayOpen && (
                <div className="una-overlay" onClick={handleCloseCreateTicketOverlay}>
                    <div className="una-overlay-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('createNewEdnTicketTitle', 'Create New EnvoiDuNet Ticket')}</h2>
                        <form onSubmit={handleSubmitNewTicket}>
                            <div className="una-form-group">
                                <label htmlFor="tracking">{t('trackingNumber', 'Tracking Number')}</label>
                                <input 
                                    type="text" 
                                    id="tracking" 
                                    name="tracking" 
                                    value={newTicketData.tracking}
                                    onChange={handleNewTicketInputChange}
                                    required 
                                />
                            </div>
                            <div className="una-form-group">
                                <label htmlFor="message">{t('message', 'Message')}</label>
                                <textarea 
                                    id="message" 
                                    name="message" 
                                    value={newTicketData.message}
                                    onChange={handleNewTicketInputChange}
                                    required 
                                    rows="4"
                                />
                            </div>
                            <div className="una-form-group">
                                <label htmlFor="ticket_topic_id">{t('ticketTopic', 'Ticket Topic')}</label>
                                <select
                                    id="ticket_topic_id"
                                    name="ticket_topic_id"
                                    value={newTicketData.ticket_topic_id}
                                    onChange={handleNewTicketInputChange}
                                    required
                                >
                                    <option value="">{t('selectTopic', '-- Select a topic --')}</option>
                                    {EDN_TICKET_TOPICS.map(topic => (
                                        <option key={topic.id} value={topic.id}>
                                            {topic.topic_full}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {submitTicketError && <p className="una-error-message">{submitTicketError}</p>}
                            {submitTicketSuccess && <p className="una-success-message">{submitTicketSuccess}</p>}
                            <div className="una-form-actions">
                                <button type="button" onClick={handleCloseCreateTicketOverlay} className="una-button una-button-secondary">
                                    {t('cancel', 'Cancel')}
                                </button>
                                <button type="submit" className="una-button una-button-primary" disabled={isSubmittingTicket}>
                                    {isSubmittingTicket ? t('submitting', 'Submitting...') : t('createTicket', 'Create Ticket')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Dashboard;